import React, { Fragment, PureComponent } from 'react';
import PropTypes from 'prop-types';
import Translator from 'translator';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import {
    withStyles,
    Card,
    CardHeader,
    CardContent,
    Typography,
    IconButton,
    CircularProgress,
    Tooltip,
    MenuItem,
    Menu,
    Grid
} from '@material-ui/core';
import { Add as AddIcon, Close as CloseIcon, Check as CheckIcon, MoreVert as MoreVertIcon } from '@material-ui/icons';
import { Toastr } from 'data/common';
import { FHETextField } from 'data/components';
import selectors from '../selectors';

class UserCard extends PureComponent {
    _componentIsMounted = true;

    static propTypes = {
        classes: PropTypes.object.isRequired,
        user: PropTypes.object,
        userId: PropTypes.number,
        addUserFunc: PropTypes.func,
        deleteUserFunc: PropTypes.func,
        editUserFunc: PropTypes.func,
        resetPasswordFunc: PropTypes.func,
        actionDisabled: PropTypes.bool,
        deleteUserRight: PropTypes.bool,
        resetUserPasswordRight: PropTypes.bool,
        editUserRight: PropTypes.bool,
        modeAdd: PropTypes.bool,
        toggleActions: PropTypes.func,
        noCancel: PropTypes.bool,
        readyToEdit: PropTypes.bool
    };
    static defaultProps = {
        user: null,
        userId: null,
        addUserFunc: null,
        deleteUserFunc: null,
        editUserFunc: null,
        resetPasswordFunc: null,
        actionDisabled: false,
        editUserRight: false,
        deleteUserRight: false,
        resetUserPasswordRight: false,
        modeAdd: false,
        toggleActions: null,
        noCancel: false,
        readyToEdit: false
    };

    constructor(props) {
        super(props);
        const { user, readyToEdit } = props;
        this.state = {
            anchorEl: null,
            loading: false,
            editing: readyToEdit,
            openedMenu: false,
            userForm: {
                lastname: user && user.get('lastname') ? user.get('lastname') : '',
                firstname: user && user.get('firstname') ? user.get('firstname') : '',
                email: user && user.get('email') ? user.get('email') : '',
                telFixe: user && user.get('telFixe') ? user.get('telFixe') : null,
                telPort: user && user.get('telPort') ? user.get('telPort') : null
            },
            textInput_error: {}
        };
    }

    componentWillUnmount() {
        this._componentIsMounted = false;
    }

    isEmpty = value => {
        return value === null || /^\s*$/.test(value);
    };

    /* STATE */
    resetInputs = () => {
        const { user } = this.props;
        this.setState({
            editing: false,
            userForm: {
                lastname: user && user.get('lastname') ? user.get('lastname') : '',
                firstname: user && user.get('firstname') ? user.get('firstname') : '',
                email: user && user.get('email') ? user.get('email') : '',
                telFixe: user && user.get('telFixe') ? user.get('telFixe') : null,
                telPort: user && user.get('telPort') ? user.get('telPort') : null
            }
        });
    };
    openMenuState = e => {
        this.setState({ openedMenu: true, anchorEl: e.currentTarget });
    };
    closeMenuState = () => {
        this.setState({ openedMenu: false });
    };
    toggleEditingState = () => {
        const { editing } = this.state;
        this.setState({ editing: !editing, openedMenu: false });
    };
    toggleLoading = () => {
        const { loading } = this.state;
        this.setState({ loading: !loading });
    };
    removeKey = (myObj, deleteKey) => {
        return Object.assign(
            {},
            ...Object.entries(myObj)
                .filter(([k]) => k !== deleteKey)
                .map(([k, v]) => ({ [k]: v }))
        );
    };
    handleChange = event => {
        const { userForm, textInput_error } = this.state;
        let targetValue = event.target.value;
        const fieldLengthRules = {
            lastname: 255,
            firstname: 255,
            email: 200,
            telFix: 20,
            telPort: 20
        };

        let newTextInput_error = this.removeKey(textInput_error, event.target.name);

        let maxLength = fieldLengthRules[event.target.name];
        if (maxLength && targetValue.length > maxLength) targetValue = userForm[event.target.name];
        if (targetValue === '') targetValue = null;

        this.setState({
            userForm: { ...userForm, [event.target.name]: targetValue },
            textInput_error: newTextInput_error
        });
    };

    handleFormError = fields => {
        let res = {};
        Object.keys(fields).map(field => {
            fields[field].forEach(error => {
                if (res[field] === undefined) res[field] = [];

                res[field].push(
                    <p key={error} className="mb-1">
                        {Translator.translate(`general.fields.error.${error}`)}
                    </p>
                );
            });
        });

        this.setState({
            textInput_error: res
        });
    };

    /* ACTIONS */
    saveUser = () => {
        const { editUserFunc, addUserFunc, modeAdd, userId } = this.props;
        const { userForm } = this.state;

        if (this.validateForm(userForm)) {
            this.toggleLoading();
            if (modeAdd) {
                addUserFunc(userForm)
                    .then(() => {
                        if (this._componentIsMounted) this.resetInputs();
                    })
                    .catch(error => {
                        if (error.validation && error.validation.errors) {
                            return this.handleFormError(error.validation.errors);
                        }
                    })
                    .finally(() => {
                        if (this._componentIsMounted) this.toggleLoading();
                    });
            } else {
                editUserFunc(userId, userForm)
                    .then(() => {
                        this.toggleEditingState();
                    })
                    .catch(error => {
                        if (error.validation && error.validation.errors) {
                            return this.handleFormError(error.validation.errors);
                        }
                    })
                    .finally(() => {
                        this.toggleLoading();
                    });
            }
        }
    };
    resetPassword = () => {
        const { userId, resetPasswordFunc, toggleActions } = this.props;
        const { userForm } = this.state;
        toggleActions(true);
        Toastr.confirm(
            Translator.translate('generic.user.actions.confirmResetPassword', userForm.firstname, userForm.lastname),
            Translator.translate('generic.user.actions.confirmResetPasswordTitle'),
            {
                onYes: () => {
                    resetPasswordFunc(userId)
                        .then(() => {
                            Toastr.success(Translator.translate('generic.user.actions.resetPasswordSuccess', userForm.firstname, userForm.lastname));
                        })
                        .catch(() => {
                            Toastr.error(Translator.translate('generic.user.actions.resetPasswordError', userForm.firstname, userForm.lastname));
                        });
                },
                onNo: () => {
                    toggleActions(false);
                }
            }
        );
    };
    deleteUser = () => {
        const { userId, deleteUserFunc, toggleActions } = this.props;
        const { userForm } = this.state;

        toggleActions(true);
        this.setState({ loading: true, openedMenu: false });
        Toastr.confirm(
            Translator.translate('generic.user.actions.confirmRemove', userForm.firstname, userForm.lastname),
            Translator.translate('generic.user.actions.confirmRemoveTitle'),
            {
                onYes: () => {
                    deleteUserFunc(userId).catch(() => {
                        this.setState({ loading: false });
                    });
                },
                onNo: () => {
                    toggleActions(false);
                    this.setState({ loading: false });
                }
            }
        );
    };
    validateForm(form) {
        const errors = {};
        const regexTel = RegExp(/^(\+[1-9]{2}\s|0)([1-9])((\s|\.|-)?\d{2}){4}$/);

        if (this.isEmpty(form.firstname)) {
            errors['firstname'] = Translator.translate('general.fields.error.any.empty');
        }
        if (this.isEmpty(form.lastname)) {
            errors['lastname'] = Translator.translate('general.fields.error.any.empty');
        }
        if (this.isEmpty(form.email)) {
            errors['email'] = Translator.translate('general.fields.error.any.empty');
        } else if (!/^([a-zA-Z0-9_.+-])+@(([a-zA-Z0-9-])+\.)+([a-zA-Z0-9]{2,4})+$/.test(form.email)) {
            errors['email'] = Translator.translate('general.fields.error.string.regex.base');
        }
        if (this.isEmpty(form.telPort) && this.isEmpty(form.telFixe)) {
            errors['telPort'] = Translator.translate('general.fields.error.any.optional');
            errors['telFixe'] = Translator.translate('general.fields.error.any.optional');
        }
        if (!this.isEmpty(form.telPort) && !regexTel.test(form.telPort)) {
            errors['telPort'] = Translator.translate('general.fields.error.string.regex.base');
        }
        if (!this.isEmpty(form.telFixe) && !regexTel.test(form.telFixe)) {
            errors['telFixe'] = Translator.translate('general.fields.error.string.regex.base');
        }

        this.setState({
            textInput_error: errors
        });

        return Object.keys(errors).length <= 0;
    }

    /* RENDERS */
    renderSaveIcon() {
        const { classes, noCancel } = this.props;
        const { loading } = this.state;

        if (loading) return <CircularProgress />;

        return (
            <Fragment>
                {!noCancel && (
                    <Tooltip title={Translator.translate('general.cancel')} placement="top">
                        <IconButton aria-label="EditUser" onClick={this.resetInputs}>
                            <CloseIcon className={classes.iconSize} />
                        </IconButton>
                    </Tooltip>
                )}
                <Tooltip title={Translator.translate('general.save')} placement="top">
                    <IconButton aria-label="EditUser" onClick={this.saveUser}>
                        <CheckIcon className={`${classes.iconSize} ${classes.primaryColor}`} />
                    </IconButton>
                </Tooltip>
            </Fragment>
        );
    }
    renderCardContent() {
        const { classes } = this.props;
        const { userForm } = this.state;

        return (
            <Fragment>
                <Typography color="textSecondary" className={classes.contactText}>
                    {userForm.email}
                </Typography>

                <Typography color="textSecondary" className={classes.contactText}>
                    {userForm.telFixe}
                </Typography>

                <Typography color="textSecondary" className={classes.contactText}>
                    {userForm.telPort}
                </Typography>
            </Fragment>
        );
    }
    renderEditingCardContent() {
        const { userForm } = this.state;

        const { textInput_error } = this.state;
        return (
            <Grid container spacing={2}>
                <Grid container item sm={12} md={6}>
                    <FHETextField
                        id="lastname"
                        value={userForm.lastname}
                        error={textInput_error.lastname}
                        fullWidth
                        label={Translator.translate('general.table.column.lastname')}
                        name="lastname"
                        onChange={this.handleChange}
                    />
                </Grid>
                <Grid container item sm={12} md={6}>
                    <FHETextField
                        id="firstname"
                        value={userForm.firstname}
                        error={textInput_error.firstname}
                        fullWidth
                        label={Translator.translate('general.table.column.firstname')}
                        name="firstname"
                        onChange={this.handleChange}
                    />
                </Grid>
                <Grid container item sm={12}>
                    <FHETextField
                        id="email"
                        value={userForm.email}
                        error={textInput_error.email}
                        fullWidth
                        label={Translator.translate('general.table.column.email')}
                        name="email"
                        onChange={this.handleChange}
                    />
                </Grid>
                <Grid container item sm={12} md={6}>
                    <FHETextField
                        id="telFixe"
                        value={userForm.telFixe}
                        error={textInput_error.telFixe}
                        fullWidth
                        label={Translator.translate('general.table.column.telFixe')}
                        name="telFixe"
                        onChange={this.handleChange}
                    />
                </Grid>
                <Grid container item sm={12} md={6}>
                    <FHETextField
                        id="telPort"
                        value={userForm.telPort}
                        error={textInput_error.telPort}
                        fullWidth
                        label={Translator.translate('general.table.column.telPort')}
                        name="telPort"
                        onChange={this.handleChange}
                    />
                </Grid>
            </Grid>
        );
    }
    renderCardActions() {
        const { actionDisabled, resetUserPasswordRight, deleteUserRight, editUserRight, classes } = this.props;
        const { editing, openedMenu, anchorEl } = this.state;

        let menuItems = [];
        if (editUserRight) {
            menuItems.push(
                <MenuItem key="edit" onClick={this.toggleEditingState}>
                    {Translator.translate('general.edit')}
                </MenuItem>
            );
        }
        if (deleteUserRight) {
            menuItems.push(
                <MenuItem key="remove" onClick={this.deleteUser}>
                    {Translator.translate('generic.user.actions.removeTitle')}
                </MenuItem>
            );
        }
        if (resetUserPasswordRight) {
            menuItems.push(
                <MenuItem key="resetPassword" onClick={this.resetPassword}>
                    {Translator.translate('generic.user.actions.resetPassword')}
                </MenuItem>
            );
        }

        if (editing) return <div className={`d-flex ${classes.header}`}>{this.renderSaveIcon()}</div>;

        return (
            <div className={`d-flex ${classes.header}`}>
                {menuItems.length > 0 && (
                    <Fragment>
                        <IconButton
                            aria-label="More"
                            aria-controls="long-menu"
                            aria-haspopup="true"
                            disabled={actionDisabled}
                            onClick={this.openMenuState}
                        >
                            <MoreVertIcon size={25} />
                        </IconButton>
                        <Menu id="long-menu" anchorEl={anchorEl} keepMounted open={openedMenu} onClose={this.closeMenuState}>
                            {menuItems}
                        </Menu>
                    </Fragment>
                )}
            </div>
        );
    }
    render() {
        const { classes, modeAdd } = this.props;
        const { editing, userForm, loading } = this.state;
        if (loading) {
            return (
                <Card className={`${classes.homeUserCardContainer} align-self-center align-items-center justify-content-center d-flex`}>
                    <CardContent className={classes.cardContent}>
                        <div className={classes.loadingContainer}>
                            <CircularProgress />
                        </div>
                    </CardContent>
                </Card>
            );
        }

        if (modeAdd && !editing) {
            return (
                <Card className={`${classes.homeUserCardContainer} align-self-center align-items-center justify-content-center d-flex`}>
                    <Tooltip title={Translator.translate('generic.user.add')} placement="top">
                        <IconButton aria-label="AddUser" onClick={this.toggleEditingState}>
                            <AddIcon className={`${classes.addIconSize} ${classes.primaryColor}`} />
                        </IconButton>
                    </Tooltip>
                </Card>
            );
        }

        return (
            <Card className={classes.homeUserCardContainer}>
                <CardHeader
                    title={!editing && `${userForm.lastname} ${userForm.firstname}`}
                    className={`${classes.cardHeader} ${classes.primaryColor}`}
                    action={this.renderCardActions()}
                    classes={{ title: classes.headerTitle }}
                />
                <CardContent className={classes.cardContent}>{editing ? this.renderEditingCardContent() : this.renderCardContent()}</CardContent>
            </Card>
        );
    }
}

function mapStateToProps(state) {
    return {
        profile: selectors.getProfile(state)
    };
}
const styles = theme => ({
    headerTitle: {
        marginBottom: 10
    },
    loadingContainer: {
        textAlign: 'center',
        margin: 20
    },
    header: {
        height: 50
    },
    iconSize: {
        fontSize: 25
    },
    addIconSize: {
        fontSize: 40
    },
    primaryColor: {
        color: theme.palette.primary.main
    },

    homeUserCardContainer: {
        height: '90%',
        width: '100%',
        marginTop: 10,
        marginBottom: 10,
        minHeight: 135
    },

    cardHeader: {
        padding: '15px 15px 0px 15px'
    },

    cardContent: {
        padding: '0px 15px 15px 15px'
    },
    nameText: {
        overflowWrap: 'break-word',
        [theme.breakpoints.down('sm')]: {
            fontSize: '1.3em'
        },
        [theme.breakpoints.down('xs')]: {
            fontSize: '1.1em'
        }
    },
    contactText: {
        overflowWrap: 'break-word',
        [theme.breakpoints.down('sm')]: {
            fontSize: '0.9em'
        },
        [theme.breakpoints.down('xs')]: {
            fontSize: '0.8em'
        }
    }
});

export default withRouter(connect(mapStateToProps)(withStyles(styles, { withTheme: true })(UserCard)));



// WEBPACK FOOTER //
// ./environments/fhe/scenes/Home/components/UserCard.js